import { escreverDecimal, lerDecimal } from '@equiview/domain';
import { useEffect, useState, type ReactNode } from 'react';
import { Campo, classeCampo } from './ui/index.js';

/**
 * Enter leva ao proximo campo da coleta, na ordem da tela: o registrador digita
 * a medida ditada e ja esta no campo seguinte, sem tocar na tela. So entram os
 * campos marcados com `data-coleta` e habilitados.
 */
export function focarProximoCampo(atual: HTMLElement) {
  const campos = [...document.querySelectorAll<HTMLElement>('[data-coleta]:not(:disabled)')];
  const i = campos.indexOf(atual);
  const proximo = campos[i + 1];
  if (proximo) {
    proximo.focus();
    proximo.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  } else {
    atual.blur();
  }
}

/**
 * Numero digitado em campo, salvo ao sair do campo. Aceita virgula (o teclado
 * do tablet em pt-BR) e arredonda para as casas da medida. Fora da faixa
 * plausivel so AVISA: quem decide e a validacao do protocolo, na hora de fechar.
 */
export function CampoNumero({
  rotulo,
  valor,
  casas,
  unidade,
  faixa,
  ajuda,
  desabilitado,
  onSalvar,
}: {
  rotulo: string;
  valor: number | null;
  casas: number;
  unidade: string;
  faixa?: { min: number; max: number };
  ajuda?: ReactNode;
  desabilitado?: boolean;
  onSalvar: (v: number | null) => Promise<void>;
}) {
  const [texto, setTexto] = useState(() => escreverDecimal(valor, casas));
  const [editando, setEditando] = useState(false);
  const [erro, setErro] = useState<string>();

  // O valor salvo muda por fora (sync, outro campo): so reflete fora da digitacao.
  useEffect(() => {
    if (!editando) setTexto(escreverDecimal(valor, casas));
  }, [valor, casas, editando]);

  const lido = lerDecimal(texto, casas);
  const fora = faixa && lido != null && !Number.isNaN(lido) && (lido < faixa.min || lido > faixa.max);
  const textoFaixa = faixa && `faixa ${escreverDecimal(faixa.min, 0)}–${escreverDecimal(faixa.max, 0)} ${unidade}`;

  const sair = async () => {
    setEditando(false);
    if (lido != null && Number.isNaN(lido)) {
      setErro('Digite só o número, com vírgula para os decimais.');
      return;
    }
    setErro(undefined);
    if (lido === valor) {
      setTexto(escreverDecimal(valor, casas));
      return;
    }
    try {
      await onSalvar(lido);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Campo
      rotulo={rotulo}
      erro={erro}
      ajuda={
        fora ? (
          <span className="font-medium text-aviso">Fora da {textoFaixa}. Confira antes de seguir.</span>
        ) : (
          [ajuda, textoFaixa].filter(Boolean).map((p, i) => (
            <span key={i}>
              {i > 0 && ' · '}
              {p}
            </span>
          ))
        )
      }
    >
      {(a) => (
        <div className="flex items-center gap-2">
          <input
            {...a}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            enterKeyHint="next"
            disabled={desabilitado}
            value={texto}
            onFocus={() => setEditando(true)}
            onChange={(e) => setTexto(e.target.value)}
            onBlur={() => void sair()}
            data-coleta=""
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              focarProximoCampo(e.currentTarget);
            }}
            className={`${classeCampo(!!erro)} numero max-w-36 text-right text-lg ${fora ? 'border-aviso' : ''}`}
          />
          <span className="text-sm text-apagado">{unidade}</span>
        </div>
      )}
    </Campo>
  );
}
